// Lab 2 deterministic scenarios. View establishment is composed from shared
// builders so failover, partition, and outage traces read as extensions of
// a healthy cluster rather than repeated setup.
import { defaultConfig } from './model';

const FIRE = (to, key) => ({ op: 'fire', select: { kind: 'timer', to, timerKey: key } });
const PING = (s) => FIRE(s, 'ping');
const CHECK = () => FIRE('V', 'check');
const PV = (s) => ({ op: 'deliver', select: { kind: 'message', from: s, to: 'V', msgType: 'ping' } });
const VV = (s) => ({ op: 'deliver', select: { kind: 'message', from: 'V', to: s, msgType: 'view' } });
const TRANSFER = () => ({ op: 'deliver', select: { kind: 'message', msgType: 'transfer' } });
const TRANSFER_REPLY = () => ({ op: 'deliver', select: { kind: 'message', msgType: 'transferreply' } });
const FORWARD = () => ({ op: 'deliver', select: { kind: 'message', msgType: 'forward' } });
const FORWARD_REPLY = () => ({ op: 'deliver', select: { kind: 'message', msgType: 'forwardreply' } });
const CREQUEST = () => ({ op: 'deliver', select: { kind: 'message', msgType: 'crequest' } });
const CREQUEST_TO = (t) => ({ op: 'deliver', select: { kind: 'message', to: t, msgType: 'crequest' } });
const CREPLY = () => ({ op: 'deliver', select: { kind: 'message', msgType: 'creply' } });
const VIEWQUERY = () => ({ op: 'deliver', select: { kind: 'message', msgType: 'viewquery' } });
const INVOKE = (command) => ({ op: 'submit', to: 'C1', command, label: 'invoke' });
const CMD = () => ({ op: 'deliver', select: { kind: 'clientCommand', to: 'C1' } });
const RETRY = (seq) => FIRE('C1', 'retry:' + seq);
// Far-future jump that lets the view server declare crashed servers dead.
const QUIESCE = { op: 'advance', to: 1000 };

const PUT_X = { op: 'put', key: 'x', value: 1 };
const PUT_Y = { op: 'put', key: 'y', value: 2 };
const PUT_Z = { op: 'put', key: 'z', value: 3 };

export function view1Established() {
  return [
    PING('S1'), PV('S1'), VV('S1'),
    CHECK(),
    PING('S1'), PV('S1'), VV('S1'),
    PING('S1'), PV('S1'), VV('S1'),
  ];
}

export function view2Established() {
  return [
    ...view1Established(),
    PING('S2'), PV('S2'), VV('S2'),
    CHECK(),
    PING('S2'), PV('S2'), VV('S2'),
    PING('S1'), PV('S1'), VV('S1'),
    TRANSFER(), TRANSFER_REPLY(),
    PING('S1'), PV('S1'), VV('S1'),
  ];
}

function firstOp() {
  return [INVOKE(PUT_X), CMD(), VIEWQUERY(), VV('C1'), CREQUEST(), FORWARD(), FORWARD_REPLY(), CREPLY()];
}

export function initialPrimary() {
  return { config: defaultConfig(), seed: 1, decisions: view1Established() };
}

export function backupJoin() {
  return { config: defaultConfig(), seed: 2, decisions: view2Established() };
}

export function normalReplicatedOp() {
  return { config: defaultConfig(), seed: 3, decisions: [...view2Established(), ...firstOp()] };
}

export function droppedForward() {
  return {
    config: defaultConfig(),
    seed: 4,
    decisions: [
      ...view2Established(),
      INVOKE(PUT_X), CMD(), VIEWQUERY(), VV('C1'), CREQUEST(),
      { ...FORWARD(), op: 'drop' },
      FIRE('S1', 'op'),
      FORWARD(), FORWARD_REPLY(), CREPLY(),
    ],
  };
}

function failoverToS2(crashFirst = true) {
  return [
    ...(crashFirst ? [{ op: 'crash', node: 'S1' }] : []),
    QUIESCE,
    PING('S2'), PV('S2'), VV('S2'),
    CHECK(),
    PING('S2'), PV('S2'), VV('S2'),
    PING('S2'), PV('S2'), VV('S2'),
  ];
}

export function primaryCrashFailover() {
  return {
    config: defaultConfig(),
    seed: 5,
    decisions: [
      ...view2Established(),
      ...firstOp(),
      ...failoverToS2(),
      INVOKE(PUT_Y), CMD(),
      RETRY(2), RETRY(2), RETRY(2),
      VIEWQUERY(), VV('C1'),
      CREQUEST_TO('S2'), CREPLY(),
    ],
  };
}

export function oldPrimaryReturns() {
  return {
    config: defaultConfig(),
    seed: 6,
    decisions: [
      ...view2Established(),
      ...firstOp(),
      ...failoverToS2(),
      INVOKE(PUT_Y), CMD(),
      RETRY(2), RETRY(2), RETRY(2),
      VIEWQUERY(), VV('C1'),
      CREQUEST_TO('S2'), CREPLY(),
      { op: 'restart', node: 'S1' },
      PING('S1'), PV('S1'), VV('S1'),
      INVOKE(PUT_Z), CMD(), CREQUEST_TO('S2'), CREPLY(),
    ],
  };
}

export function partitionedPrimary() {
  return {
    config: defaultConfig(),
    seed: 7,
    decisions: [
      ...view2Established(),
      { op: 'partition', setA: ['S1'], setB: ['V', 'S2', 'C1'] },
      ...failoverToS2(false),
      INVOKE(PUT_Y), CMD(),
      VIEWQUERY(), VV('C1'),
      CREQUEST_TO('S2'), CREPLY(),
      { op: 'heal', setA: ['S1'], setB: ['V', 'S2', 'C1'] },
      PING('S1'), PV('S1'), VV('S1'),
    ],
  };
}

export function unackedDeadlock() {
  return {
    config: defaultConfig(),
    seed: 8,
    decisions: [
      ...view1Established(),
      PING('S2'), PV('S2'), VV('S2'),
      CHECK(),
      { op: 'crash', node: 'S1' },
      QUIESCE,
      CHECK(), CHECK(), CHECK(),
    ],
  };
}

// The view server is a single point of failure for view *changes*, but
// steady-state work continues on cached views while it is down.
export function viewServerOutage() {
  return {
    config: defaultConfig(),
    seed: 9,
    decisions: [
      ...view2Established(),
      ...firstOp(),
      { op: 'crash', node: 'V' },
      INVOKE(PUT_Y), CMD(), CREQUEST(), FORWARD(), FORWARD_REPLY(), CREPLY(),
      { op: 'restart', node: 'V' },
      PING('S1'), PV('S1'), VV('S1'),
      PING('S2'), PV('S2'), VV('S2'),
      CHECK(),
    ],
  };
}

export const ALL_SCENARIOS = {
  initialPrimary,
  backupJoin,
  normalReplicatedOp,
  droppedForward,
  primaryCrashFailover,
  oldPrimaryReturns,
  partitionedPrimary,
  unackedDeadlock,
  viewServerOutage,
};
